import type { CampaignPageContent } from './campaign-page-content-types';

/**
 * The eight campaign page sections in render order. Keys line up with the content object (plus `footer`, which
 * renders salon profile facts), so the editor cards, issue banners and the renderer's anchors share one name set.
 */
export const CAMPAIGN_PAGE_SECTION_KEYS = [
  'brand',
  'top',
  'poster',
  'form',
  'text',
  'reviews',
  'gallery',
  'footer',
] as const;
export type CampaignPageSectionKey = (typeof CAMPAIGN_PAGE_SECTION_KEYS)[number];

/** Short owner-facing names — used to group validation issues and in "what changes" banners. */
export const CAMPAIGN_PAGE_SECTION_LABELS: Record<CampaignPageSectionKey, string> = {
  brand: 'Brand & colors',
  top: 'Headline',
  poster: 'Flyer',
  form: 'Button & form',
  text: 'Your text',
  reviews: 'Reviews',
  gallery: 'Photos',
  footer: 'Footer',
};

/** Sections that own content, in page order (the footer has none). */
export const CAMPAIGN_PAGE_CONTENT_SECTION_KEYS = [
  'brand',
  'top',
  'poster',
  'form',
  'text',
  'reviews',
  'gallery',
] as const satisfies readonly CampaignPageSectionKey[];
export type CampaignPageContentSectionKey = (typeof CAMPAIGN_PAGE_CONTENT_SECTION_KEYS)[number];

/**
 * Map a validation issue path (`top.highlights.1.big`) to the section that owns it.
 * `footer` has no content and `version` is internal, so those yield null.
 */
export function sectionKeyForCampaignPageIssuePath(path: string): CampaignPageContentSectionKey | null {
  const root = path.split('.')[0];
  return CAMPAIGN_PAGE_CONTENT_SECTION_KEYS.find((key) => key === root) ?? null;
}

export interface CampaignPageSummary {
  hasFlyer: boolean;
  hasHeadline: boolean;
  hasText: boolean;
  highlightCount: number;
}

/** Quick facts for the hub card / readiness checklist. */
export function summarizeCampaignPage(content: CampaignPageContent): CampaignPageSummary {
  return {
    hasFlyer: content.poster.imageUrl.length > 0,
    hasHeadline: content.top.headline.trim().length > 0,
    hasText: content.text.body.trim().length > 0,
    highlightCount: content.top.highlights.filter((row) => row.big.trim().length > 0).length,
  };
}

/** Key-order-independent JSON, so a re-serialised but equal section never reads as changed. */
function stableStringify(value: unknown): string {
  if (Array.isArray(value)) return `[${value.map(stableStringify).join(',')}]`;
  if (value && typeof value === 'object') {
    const entries = Object.entries(value as Record<string, unknown>)
      .filter(([, entry]) => entry !== undefined)
      .sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0));
    return `{${entries.map(([key, entry]) => `${JSON.stringify(key)}:${stableStringify(entry)}`).join(',')}}`;
  }
  return JSON.stringify(value ?? null);
}

/**
 * Sections whose draft differs from the live page, in page order — "what changes when you publish".
 * Pass the draft normalised the way publish stores it (trimmed) so whitespace-only edits don't show.
 */
export function changedCampaignPageSections(
  draft: CampaignPageContent,
  published: CampaignPageContent
): CampaignPageContentSectionKey[] {
  return CAMPAIGN_PAGE_CONTENT_SECTION_KEYS.filter(
    (key) => stableStringify(draft[key]) !== stableStringify(published[key])
  );
}
